import {
  collection,
  doc,
  endAt,
  getDocs,
  increment,
  limit,
  orderBy,
  Query,
  QueryConstraint,
  query,
  runTransaction,
  serverTimestamp,
  startAt,
  Timestamp
} from "firebase/firestore";
import {RootState} from "../store/store";
import {db} from "../../firebase";
import {orderConverter, productConverter} from "../../firebase/converters";
import {createAsyncThunk} from "@reduxjs/toolkit";
import {Order} from "./types";
import {OrderByDirection} from "firebase/firestore";
import {addFetchedPage, setFromIndex, setToIndex} from "./slice";
import {getDateDiff, getYYYYMMDD} from "../../utils/dateUtils";
import toast from "react-hot-toast";

export type OrderQueryArgs = {
  direction: OrderByDirection,
  from: Date | undefined,
  to: Date | undefined,
}

/**
 * ある日付以降を表すクエリ. direction が asc なら startAt, desc なら endAt
 */
const startFrom = (direction: OrderByDirection, fieldValue: unknown) => direction === 'asc' ? startAt(fieldValue) : endAt(fieldValue);

/**
 * ある日付以前を表すクエリ. direction が asc なら endAt, desc なら startAt
 */
const endTo = (direction: OrderByDirection, fieldValue: unknown) => direction === 'asc' ? endAt(fieldValue) : startAt(fieldValue);

async function fetchCursorOrder(ordersRef: Query<Order>, cursorTimestamp: Timestamp | undefined, cursorType: 'from' | 'to') {
  const _startAt = cursorTimestamp ? [startAt(cursorTimestamp)] : [];
  const q = query(ordersRef, limit(1), orderBy('created_at', cursorType === 'from' ? 'asc' : 'desc'), ..._startAt);

  try {
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      return snapshot.docs[0].data();
    } else {
      // from 以降のデータがないので, 空配列を返す
      return undefined;
    }
  } catch (e) {
    console.error(e);
    return undefined;
  }
}

/**
 * 指定したクエリカーソル以降のuserの注文履歴を取得する
 * @param lim 取得する最大数
 * @param sortType ソートの種類
 * @param cursor endBefore/startAfterで指定するタイムスタンプ
 */
export const fetchOrders = createAsyncThunk<
  Order[],
  { lim: number, page: number, sort: OrderQueryArgs },
  { state: RootState }
>("orders/fetchOrders", async ({lim, page, sort}, {getState, dispatch, rejectWithValue}) => {
    const state = getState();
    const uid = state.users.uid;
    const orderInfo = state.info.orderInfo.data;
    const {fetchedPages, fromIndex, toIndex} = state.orders;
    const {from, to, direction} = sort;
    // このページ, この前のページ, この次のページをフェッチしたかどうか
    const isFetchedThis = fetchedPages.includes(page);

    // クエリに必要な index のカーソルを取得
    if (isFetchedThis) {
      // 既にフェッチ済みの場合は空配列を返す
      return [];
    } else {
      // from~toの最初と最後を取ってきて, 取得したいpageの部分をindexで指定して取得する

      let _fromIndex = fromIndex ?? 1;
      let _toIndex = toIndex ?? 1;
      const ordersRef = collection(db, 'users', uid, 'orders').withConverter(orderConverter);

      if (fromIndex === undefined && from !== undefined) {
        const cursor = await fetchCursorOrder(ordersRef, Timestamp.fromDate(from), 'from');

        if (cursor) _fromIndex = cursor.index;
        else return []; // from 以降の order が存在しない => 該当するデータなし
      }

      if (toIndex === undefined && to !== undefined) {
        const cursor = await fetchCursorOrder(ordersRef, Timestamp.fromDate(to), 'to');

        if (cursor) _toIndex = cursor.index;
        else return []; // to 以前の order が存在しない => 該当するデータなし
      } else if (direction === 'desc') {
        // to が指定されてなくても降順なら最大のindexを取得する
        if (orderInfo) {
          _toIndex = orderInfo.latestIndex;
        } else {
          const cursor = await fetchCursorOrder(ordersRef, undefined, 'to');

          if (cursor) _toIndex = cursor.index;
        }
      }

      // storeに保存
      dispatch(setFromIndex(_fromIndex));
      dispatch(setToIndex(_toIndex));
      dispatch(addFetchedPage(page));

      // index を page の部分までずらす
      const indexOffset = (page - 1) * lim;
      if (direction === 'asc') {
        _fromIndex += indexOffset;
      } else {
        _toIndex -= indexOffset;
      }

      // Orders を取得
      const queryConstraints: QueryConstraint[] = [limit(lim), orderBy('index', direction)];

      queryConstraints.push(startFrom(direction, _fromIndex)); // fromの開始点から page の表示点を求める
      if (_toIndex !== 0) queryConstraints.push(endTo(direction, _toIndex)); // to は終点なので, page を考える必要はない

      const q = query(ordersRef, ...queryConstraints);

      try {
        const snapshots = await getDocs(q);
        return snapshots.docs.map(doc => doc.data());

      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    }
  }
);

/**
 * 注文をキャンセルする.
 * 受け取り日の前日まででないとキャンセルできない.
 * DBの書き込み内容: products/productのordered[yyyymmdd]を商品数分減算 stocks[yyyymmdd]を商品数分加算, users/user/orders/orderIdを削除, archives/yyyymmdd/orders/{orderId}を削除
 * @param orderId キャンセル対象注文のID
 */
export const cancelOrder = createAsyncThunk<
  Partial<Order>,
  {orderId: string},
  {state: RootState}
>("orders/cancelOrder",  async ({orderId}, {getState, rejectWithValue}) => {
  const state = getState();
  const uid = state.users.uid;
  const orderDiff: Partial<Order> = {status: "canceled"};

  try {
    await runTransaction(db, async (transaction) => {
      // orderId に一致する注文を取得し, 存在しなければエラー吐く
      const orderInUserRef = doc(db, `users/${uid}/orders/${orderId}`).withConverter(orderConverter);
      try {
        const orderSnapshot = await transaction.get(orderInUserRef);
        if (!orderSnapshot.exists())
          return Promise.reject(`該当する注文が存在しません`);

        const order = orderSnapshot.data();

        // 注文のcreated_atが当日だったらエラー
        const today = new Date();
        if (getDateDiff(order.created_at.toDate(), today) <= -1)
          return Promise.reject('注文のキャンセルは前日まで可能です')

        // 各productについて更新
        const receivedyyyyMMdd = getYYYYMMDD(order.received_Timestamp.toDate());

        for (const product of order.products) {
          const productRef = doc(db, `products/${product.id}`).withConverter(productConverter);
          const quantity = product.quantity;

          try {
            // product.ordered[yyyymmdd]を減算, stockを加算
            transaction.update(productRef, {
              [`ordered.${receivedyyyyMMdd}`]: increment(-quantity),
              "updated_at": serverTimestamp(),
            }); // FIXME: 型付ける, productRefのproductが存在するか確認
          } catch (e) {
            return Promise.reject(e);
          }
        }

        // users/user/orders/orderIdを削除
        try {
          transaction.update(orderInUserRef, orderDiff);
        } catch (e) {
          return Promise.reject(e);
        }

        // archives/yyyyMMdd/orders/{orderId}を削除
        // FIXME: archives以下の注文ドキュメントのIDはランダムIDでorderIdと一致していない！
        const orderInArchiveRef = doc(db, `archives/${receivedyyyyMMdd}/orders/${orderId}`);
        try {
          transaction.update(orderInArchiveRef, orderDiff);
        } catch (e) {
          return Promise.reject(e);
        }
      } catch (e) {
        return Promise.reject(e);
      }
    });

    toast.success('注文をキャンセルしました');
    return orderDiff;
  } catch (e) {
    if (e instanceof Error) {
      toast.error(e.message);
    }
    return rejectWithValue(e);
  }
});
